import React, { useEffect, useMemo, useState } from "react";
import { Alert, Button, Input, Table, Tabs, Typography } from "antd";
import type { ColumnsType } from "antd/es/table";
import { SearchOutlined } from "@ant-design/icons";
import { Appointment } from "../../../../types/appointments/AppointmentTypes";
import { TeamMember } from "../../../../types/admin/TeamTypes";
import { Customer } from "../../../../types/inventory/CustomerTypes";
import { fetchAppointments } from "../../../../api/healthcare/Appointment/AppointmentApi";
import { fetchTeamMembers } from "../../../../api/admin/teamApi";
import { useNotifications } from "../../../../context/NotificationContext";
import InlineLoading from "../../../../components/shared/InlineLoading";
import EmptyState from "../../../../components/shared/EmptyState";
import StatusTag from "../../../../components/shared/StatusTag";
import { formatInZone, fromUtc } from "../../../../utils/time";
import type { UserUuid } from "../../../../types/identity";

const { Text } = Typography;

/**
 * CU-86d45b54n — read-only appointment history for a customer-directory row: the same healthcare read
 * the patient record uses, keyed by customer.id, with no booking, editing or consultation surface.
 */
const isCancelled = (appointment: Appointment): boolean => /^cancel/i.test(appointment.status ?? "");

const startsAfter = (appointment: Appointment, instant: number): boolean =>
	(fromUtc(appointment.start_time)?.valueOf() ?? 0) > instant;

const CustomerAppointmentsTab: React.FC<{ customer: Customer }> = ({ customer }) => {
	const [loading, setLoading] = useState(true);
	const [loadFailed, setLoadFailed] = useState(false);
	const [reloadKey, setReloadKey] = useState(0);
	const [appointments, setAppointments] = useState<Appointment[]>([]);
	const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
	const [activeTab, setActiveTab] = useState("all");
	const [searchTerm, setSearchTerm] = useState("");
	const { showError } = useNotifications();

	useEffect(() => {
		let cancelled = false;

		const load = async () => {
			setLoading(true);
			setLoadFailed(false);
			// CU-86d45b54n — clear the previous customer's rows so a failed load can never expose them.
			setAppointments([]);
			try {
				const { data } = await fetchAppointments(undefined, null, null, customer.id);
				if (cancelled) return;
				setAppointments(data ?? []);

				try {
					const { data: members } = await fetchTeamMembers(1, 100);
					if (!cancelled) setTeamMembers(members ?? []);
				} catch {
					if (!cancelled) setTeamMembers([]);
				}
			} catch {
				if (!cancelled) {
					setLoadFailed(true);
					showError("Failed to load appointments");
				}
			} finally {
				if (!cancelled) setLoading(false);
			}
		};

		void load();

		return () => {
			cancelled = true;
		};
	}, [customer.id, reloadKey]);

	const { upcoming, past, cancelledList } = useMemo(() => {
		const now = Date.now();

		return {
			upcoming: appointments.filter((a) => !isCancelled(a) && startsAfter(a, now)),
			past: appointments.filter((a) => !isCancelled(a) && !startsAfter(a, now)),
			cancelledList: appointments.filter(isCancelled),
		};
	}, [appointments]);

	const practitionerName = (id?: UserUuid): string => {
		const member = teamMembers.find((candidate) => candidate.id === id);
		return member ? `${member.first_name ?? ""} ${member.last_name ?? ""}`.trim() : "";
	};

	const query = searchTerm.trim().toLowerCase();
	const filtered = (list: Appointment[]) =>
		query
			? list.filter(
					(a) =>
						(a.title || "").toLowerCase().includes(query)
						|| practitionerName(a.practitioner_id).toLowerCase().includes(query),
				)
			: list;

	const columns: ColumnsType<Appointment> = [
		{
			title: "Appointment",
			dataIndex: "title",
			key: "title",
			render: (value: string) => <Text strong>{value || "—"}</Text>,
			sorter: (a, b) => String(a.title || "").localeCompare(String(b.title || "")),
		},
		{
			title: "Status",
			dataIndex: "status",
			key: "status",
			width: 150,
			render: (value: string) => <StatusTag status={value ?? ""} />,
		},
		{
			title: "Date & Time",
			dataIndex: "start_time",
			key: "start_time",
			width: 200,
			render: (value: Appointment["start_time"]) => formatInZone(value, "DD MMM YYYY hh:mm A") || "—",
		},
		{ title: "Location", dataIndex: "location", key: "location", width: 140, render: (v) => v || "—" },
		{
			title: "Practitioner",
			dataIndex: "practitioner_id",
			key: "practitioner",
			width: 180,
			render: (value: UserUuid) => practitionerName(value) || "—",
		},
		{ title: "Category", dataIndex: "category", key: "category", width: 180, render: (v) => v || "—" },
	];

	const emptyText = query ? "No appointments match this search" : "No appointments for this customer";

	const table = (list: Appointment[]) => (
		<Table<Appointment>
			size="small"
			rowKey={(record) => String(record.id)}
			columns={columns}
			dataSource={filtered(list)}
			pagination={{ pageSize: 10, hideOnSinglePage: true }}
			scroll={{ x: "max-content" }}
			locale={{ emptyText: <EmptyState title={emptyText} /> }}
		/>
	);

	if (loading) {
		return <InlineLoading message="Loading appointments…" minHeight={200} />;
	}

	if (loadFailed) {
		return (
			<Alert
				type="error"
				showIcon
				message="Unable to load appointments"
				description="The customer's appointment history could not be retrieved."
				action={<Button onClick={() => setReloadKey((key) => key + 1)}>Retry</Button>}
				data-testid="customer-appointments-error"
			/>
		);
	}

	return (
		<div style={{ display: "flex", flexDirection: "column", gap: 8 }} data-testid="customer-appointments">
			<div style={{ display: "flex", justifyContent: "flex-end" }}>
				<Input
					placeholder="Search appointments"
					value={searchTerm}
					onChange={(e) => setSearchTerm(e.target.value)}
					prefix={<SearchOutlined />}
					allowClear
					style={{ minWidth: 240, maxWidth: 360 }}
					data-testid="customer-appointments-search"
				/>
			</div>

			<Tabs
				activeKey={activeTab}
				onChange={setActiveTab}
				items={[
					{ key: "all", label: `All Appointments (${appointments.length})`, children: table(appointments) },
					{ key: "upcoming", label: `Upcoming (${upcoming.length})`, children: table(upcoming) },
					{ key: "past", label: `Past (${past.length})`, children: table(past) },
					{ key: "canceled", label: `Canceled (${cancelledList.length})`, children: table(cancelledList) },
				]}
			/>
		</div>
	);
};

export default CustomerAppointmentsTab;
